import { useEffect, useState, type RefObject } from "react";

export interface ElementSize {
    width: number;
    height: number;
}

export function useElementSize<T extends HTMLElement>(ref: RefObject<T | null>): ElementSize | undefined {
    const [size, setSize] = useState<ElementSize>();

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const updateSize = (width: number, height: number) => {
            setSize((current) => {
                if (current?.width === width && current?.height === height) {
                    return current;
                }

                return { width, height };
            });
        };

        const rect = element.getBoundingClientRect();
        updateSize(rect.width, rect.height);

        let frame: number | null = null;
        let pendingWidth = rect.width;
        let pendingHeight = rect.height;

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;

            pendingWidth = entry.contentRect.width;
            pendingHeight = entry.contentRect.height;

            if (frame !== null) return;

            frame = window.requestAnimationFrame(() => {
                frame = null;
                updateSize(pendingWidth, pendingHeight);
            });
        });

        observer.observe(element);

        return () => {
            observer.disconnect();
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }
        };
    }, [ref]);

    return size;
}
